// --- Dependencies -------------------------------------------------------------------------------
import { useEffect } from "react";
import { Link } from "react-router-dom";
// --- Our Modules --------------------------------------------------------------------------------
import constants from "@/config/constants";
import useFetch from "@/hooks/useFetch";
import useLang from "@/hooks/useLang";
import usePopup from "@/hooks/usePopup";
import useTranslation from "@/hooks/useTranslation";
import useUploads from "@/hooks/useUploads";
import type { TyContract, TyFile, TyHold, TyReservation } from "@/types";
import { formatBytes, formatDate, formatMoney, readLangValue } from "@/utils";
import Avatar from "@/UI/atoms/Avatar";
import Icon from "@/UI/atoms/Icon";
import type { TyIconName } from "@/UI/atoms/Icon/types";
import ActionsPopup, { type TyAction } from "@/UI/popups/ActionsPopup";
import FilePreviewPopup from "@/UI/popups/FilePreviewPopup";
import UploadFilesPopup from "@/UI/popups/UploadFilesPopup";
import "./styles.css";
// --- Logic --------------------------------------------------------------------------------------
export type TyDeal = ({ kind: "contract" } & TyContract) | ({ kind: "reservation" } & TyReservation);

export type TyDealDetails = {
    deal: TyDeal;
    /**
     * What the unit is listed at, when the screen knows it and the populated row does not — the
     * unit's own page, where the record in hand is the unit rather than the paper.
     */
    listPrice?: number | null;
    /** Cancelling. It is never on the surface: see the note on the menu below. */
    onCancel?: () => void;
};

type Props = { onClose: () => void; details?: unknown };

/**
 * One contract, or one hold, read whole — and the only place either of them is read whole.
 *
 * The card that opens this answers "which of these is the one I want"; this answers "what does it
 * say". That split is why the card carries no figures beyond the price and no buttons at all.
 *
 * **The money is drawn as a subtraction**, not as two numbers in a grid. A contract signed under
 * the unit's listed price is the single most confusing thing on a unit's screen — the page says
 * 500,000 at the top and 460,000 at the bottom and neither one is wrong — so the difference is
 * printed as what it is, a discount, rather than left for somebody to work out.
 *
 * **Cancelling is in the menu.** It is the one act here that cannot be undone, it is reached three
 * or four times a year, and a destructive button sitting under a record people open to read is a
 * button that eventually gets pressed by somebody who meant to close the popup.
 *
 * **The paper itself lives here too.** A contract row with no scan of the signed agreement is a
 * claim that an agreement exists; this is the one screen the record is read on, so it is where the
 * scan belongs and where the receipts and anything else file beside it. They are ordinary `File`
 * rows owned by the contract — the signed copy in the `paper` slot, everything else loose — so the
 * drive, the storage bill and the preview all already know about them.
 */
export default function DealPopup({ onClose, details }: Props) {
    const t = useTranslation();
    const lang = useLang((state) => state.lang) ?? "ar";
    const addComp = usePopup((state) => state.addComp);
    const uploadsFinishedAt = useUploads((state) => state.finishedAt);
    const { deal, listPrice, onCancel } = details as TyDealDetails;

    const isContract = deal.kind === "contract";
    const ownerModel = isContract ? "Contract" : "Reservation";

    /**
     * Its own request rather than a populate on the deal: a list of contracts has no business
     * carrying everybody's attachments, and this is the only place they are read.
     */
    const files = useFetch<{ files: TyFile[]; total: number }>("/drive", {}, false);

    useEffect(() => {
        files.invoke({ query: { ownerModel, ownerId: deal._id, limit: constants.maxPageSize } });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deal._id, ownerModel, uploadsFinishedAt]);

    const rows = files.data?.files ?? [];
    /** The signed agreement, told from the rest by its slot — see `FILE_SLOTS` in the backend. */
    const paper = isContract ? (rows.find((file) => file.slot === "paper") ?? null) : null;
    const rest = rows.filter((file) => file !== paper);
    const customer = typeof deal.customer === "object" && deal.customer ? deal.customer : null;
    const unit = typeof deal.unit === "object" && deal.unit ? deal.unit : null;
    const project = unit && typeof unit.project === "object" && unit.project ? unit.project : null;
    const author = typeof deal.createdBy === "object" && deal.createdBy ? deal.createdBy.name : null;

    /** The hold this paper came out of. It is closed by the sale, so only the contract still knows. */
    const before: TyHold | null = isContract && typeof deal.reservation === "object" && deal.reservation ? deal.reservation : null;

    const title = isContract ? t.properties.offers[deal.type] : t.sales.reserve;
    const state = isContract ? t.sales.contractStatuses[deal.isLive ? "live" : "cancelled"] : t.sales.reservationStatuses[deal.status];

    /**
     * What was agreed, against what the unit asks. A rent figure is not measured against a sale
     * price, so a let is left as one number — the two are not comparable and subtracting them
     * would invent a discount nobody gave.
     */
    const asked = listPrice ?? unit?.price ?? null;
    const agreed = isContract ? deal.amount : deal.price;
    const isComparable = !isContract || deal.type === "sale";
    const discount = isComparable && asked && agreed && asked > agreed ? asked - agreed : null;

    const facts: { key: string; icon: TyIconName; label: string; value: string }[] = [];
    if (isContract) {
        if (deal.signedAt) facts.push({ key: "signedAt", icon: "check", label: t.sales.signedAt, value: formatDate(deal.signedAt, lang) });
        if (deal.startsAt) facts.push({ key: "startsAt", icon: "calendar", label: t.sales.startsAt, value: formatDate(deal.startsAt, lang) });
        if (deal.endsAt) facts.push({ key: "endsAt", icon: "calendar", label: t.sales.endsAt, value: formatDate(deal.endsAt, lang) });
    } else {
        if (deal.deposit) facts.push({ key: "deposit", icon: "stat", label: t.sales.deposit, value: formatMoney(deal.deposit) });
        if (deal.expiresAt) facts.push({ key: "expiresAt", icon: "calendar", label: t.sales.expiresAt, value: formatDate(deal.expiresAt, lang) });
    }
    if (deal.createdAt) facts.push({ key: "createdAt", icon: "history", label: t.sales.recordedAt, value: formatDate(deal.createdAt, lang) });

    /** The drive's own viewer, walking this record's files and nothing else. */
    const openFile = (file: TyFile, list: TyFile[]) =>
        addComp({
            name: "file-preview",
            Component: FilePreviewPopup,
            anim: "fade",
            details: {
                files: list,
                index: Math.max(
                    0,
                    list.findIndex((item) => item._id === file._id)
                ),
            },
        });

    /**
     * Uploading says **what** the file is before it is picked, because that is the only moment
     * anybody knows: a slot written at upload is what tells the signed agreement apart from the
     * receipts filed next to it, and nothing afterwards can work it out from the bytes.
     */
    const openUpload = (slot: string | null) =>
        addComp({
            name: "upload-files",
            Component: UploadFilesPopup,
            place: "end",
            anim: "endSlide",
            details: { owner: { model: ownerModel, id: deal._id }, slot },
        });

    const openFileMenu = (anchor: DOMRect) =>
        addComp({
            name: "deal-files",
            Component: ActionsPopup,
            place: "anchor-below",
            anim: "scaleTop",
            cover: "invisible",
            details: {
                actions: [
                    ...(isContract ? [{ key: "paper", label: t.drive.slots.paper, icon: "document", onSelect: () => openUpload("paper") }] : []),
                    { key: "receipt", label: t.drive.slots.receipt, icon: "stat", onSelect: () => openUpload("receipt") },
                    { key: "other", label: t.sales.otherFile, icon: "upload", onSelect: () => openUpload(null) },
                ] as TyAction[],
            },
            anchor: { top: anchor.top, left: anchor.left, right: anchor.right, bottom: anchor.bottom, width: anchor.width, height: anchor.height },
        });

    const openMenu = (anchor: DOMRect) =>
        addComp({
            name: "deal-actions",
            Component: ActionsPopup,
            place: "anchor-below",
            anim: "scaleTop",
            cover: "invisible",
            details: {
                actions: [
                    {
                        key: "cancel",
                        label: isContract ? t.sales.cancelContract : t.sales.cancelReservation,
                        icon: "clear",
                        danger: true,
                        // The confirmation is another popup, so this one gets out of its way first.
                        onSelect: () => {
                            onClose();
                            onCancel?.();
                        },
                    },
                ] as TyAction[],
            },
            anchor: { top: anchor.top, left: anchor.left, right: anchor.right, bottom: anchor.bottom, width: anchor.width, height: anchor.height },
        });

    return (
        <section className={`deal-popup deal-popup--${deal.isLive ? "live" : "closed"}`}>
            <div className="deal-popup__chrome">
                {onCancel && deal.isLive && (
                    <button
                        type="button"
                        className="deal-popup__chrome-button"
                        onClick={(event) => openMenu(event.currentTarget.getBoundingClientRect())}
                        aria-label={t.common.more}
                    >
                        <Icon name="more" size={16} />
                    </button>
                )}
                <button type="button" className="deal-popup__chrome-button" onClick={onClose} aria-label={t.common.close}>
                    <Icon name="close" size={16} />
                </button>
            </div>

            <header className="deal-popup__hero">
                <span className="deal-popup__medallion" aria-hidden="true">
                    <Icon name={isContract ? "document" : "history"} size={26} />
                </span>

                <h2 className="deal-popup__title">{title}</h2>
                <p className="deal-popup__kind">{isContract ? t.sales.contractDetails : t.sales.reservationDetails}</p>
                <span className="deal-popup__state">{state}</span>
            </header>

            <div className="deal-popup__body">
                {/* Who and which flat — one row, two destinations, as on a guarantee. */}
                {(customer || unit) && (
                    <div className="deal-popup__links">
                        {customer && (
                            <Link className="deal-popup__link" to={`/crm/customers/${customer._id}`} onClick={onClose}>
                                <Avatar name={customer.name} picture={customer.picture ?? null} size={28} rounded />
                                <span>
                                    <small>{t.warranties.customer}</small>
                                    <strong>{customer.name}</strong>
                                </span>
                            </Link>
                        )}

                        {unit && project && (
                            <Link
                                className="deal-popup__link"
                                to={`/properties/${project.slug}/units/${encodeURIComponent(unit.number)}`}
                                onClick={onClose}
                            >
                                <span className="deal-popup__link-mark" aria-hidden="true">
                                    <Icon name="properties" size={16} />
                                </span>
                                <span>
                                    <small>{t.warranties.unit}</small>
                                    <strong>
                                        {readLangValue(project.name, lang)} · {unit.number}
                                    </strong>
                                </span>
                            </Link>
                        )}
                    </div>
                )}

                {/* ─── What it was agreed at ─────────────────────── */}
                <section className="deal-popup__money">
                    <span className="deal-popup__money-main">
                        <small>{isContract ? t.sales.amount : t.properties.fields.price}</small>
                        <strong>
                            <bdi dir="ltr">{agreed ? formatMoney(agreed) : t.properties.notSet}</bdi>
                            {!!agreed && <span className="deal-popup__currency">{t.properties.currency}</span>}
                        </strong>
                    </span>

                    {/* Only when the two differ: an equal pair is not a discount of zero, it is no
                        discount, and printing it would make every ordinary sale look negotiated. */}
                    {discount !== null && (
                        <span className="deal-popup__money-off">
                            <small>{t.sales.listPrice}</small>
                            <s>
                                <bdi dir="ltr">{formatMoney(asked!)}</bdi>
                            </s>
                            <strong>
                                {t.sales.discount}
                                <bdi dir="ltr">{formatMoney(discount)}</bdi>
                            </strong>
                        </span>
                    )}
                </section>

                {/* ─── The paper, and whatever was filed with it ─── */}
                <section className="deal-popup__papers">
                    <h3 className="deal-popup__papers-head">
                        <Icon name="document" size={13} />
                        {t.sales.papers}
                        <button
                            type="button"
                            className="deal-popup__papers-add"
                            onClick={(event) => openFileMenu(event.currentTarget.getBoundingClientRect())}
                            aria-label={t.sales.addFile}
                            title={t.sales.addFile}
                        >
                            <Icon name="add" size={14} />
                        </button>
                    </h3>

                    {/* The signed copy, given the whole width: it is the record, the rest is beside it. */}
                    {paper && (
                        <button type="button" className="deal-popup__paper" onClick={() => openFile(paper, rows)}>
                            <span className="deal-popup__paper-mark" aria-hidden="true">
                                <Icon name="document" size={18} />
                            </span>
                            <span className="deal-popup__paper-text">
                                <small>{t.drive.slots.paper}</small>
                                <strong>{paper.name}</strong>
                            </span>
                            <Icon name="eye" size={16} />
                        </button>
                    )}

                    {/*
                     * Said out loud rather than left as an empty strip. A contract with no scan on
                     * it is the one row in this product that looks complete and is not, and the
                     * person who can still fix that is the one who just opened it.
                     */}
                    {isContract && !paper && !files.loading && (
                        <button type="button" className="deal-popup__paper deal-popup__paper--missing" onClick={() => openUpload("paper")}>
                            <span className="deal-popup__paper-mark" aria-hidden="true">
                                <Icon name="upload" size={18} />
                            </span>
                            <span className="deal-popup__paper-text">
                                <small>{t.sales.noPaper}</small>
                                <strong>{t.drive.slots.paper}</strong>
                            </span>
                            <Icon name="add" size={16} />
                        </button>
                    )}

                    {rest.length > 0 && (
                        <ul className="deal-popup__files">
                            {rest.map((file) => (
                                <li key={file._id}>
                                    <button type="button" className="deal-popup__file" onClick={() => openFile(file, rows)}>
                                        <Icon name={file.category === "image" ? "image" : "document"} size={14} />
                                        <span className="deal-popup__file-name">{file.name}</span>
                                        <small>{formatBytes(file.size, lang)}</small>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}

                    {!rows.length && !files.loading && !isContract && <p className="deal-popup__papers-empty">{t.sales.noFiles}</p>}
                </section>

                {/* ─── The hold it came out of, before the paper ─── */}
                {before && (
                    <section className="deal-popup__before">
                        <h3 className="deal-popup__before-title">
                            <Icon name="history" size={13} />
                            {t.sales.priorHold}
                        </h3>
                        <dl className="deal-popup__before-facts">
                            {!!before.price && (
                                <div>
                                    <dt>{t.properties.fields.price}</dt>
                                    <dd>
                                        <bdi dir="ltr">{formatMoney(before.price)}</bdi>
                                    </dd>
                                </div>
                            )}
                            {!!before.deposit && (
                                <div>
                                    <dt>{t.sales.deposit}</dt>
                                    <dd>
                                        <bdi dir="ltr">{formatMoney(before.deposit)}</bdi>
                                    </dd>
                                </div>
                            )}
                            {!!before.expiresAt && (
                                <div>
                                    <dt>{t.sales.expiresAt}</dt>
                                    <dd>
                                        <bdi dir="ltr">{formatDate(before.expiresAt, lang)}</bdi>
                                    </dd>
                                </div>
                            )}
                        </dl>
                    </section>
                )}

                {facts.length > 0 && (
                    <dl className="deal-popup__facts">
                        {facts.map((fact) => (
                            <div key={fact.key} className="deal-popup__fact">
                                <span className="deal-popup__fact-icon" aria-hidden="true">
                                    <Icon name={fact.icon} size={14} />
                                </span>
                                <dt>{fact.label}</dt>
                                <dd>
                                    <bdi dir="ltr">{fact.value}</bdi>
                                </dd>
                            </div>
                        ))}
                    </dl>
                )}

                {/* Who filed it. A face rather than a row in the grid: it is a person, not a value. */}
                {author && (
                    <div className="deal-popup__by">
                        <Avatar name={author} size={32} rounded />
                        <span>
                            <small>{t.sales.recordedBy}</small>
                            <strong>{author}</strong>
                        </span>
                    </div>
                )}
            </div>
        </section>
    );
}
